/**
 * Defensive matchup chart for the dossier MATCHUPS tab (DR-8, 2026-05-13).
 *
 * Five rows, one per multiplier bucket: 4× / 2× / ½× / ¼× / 0×. Empty
 * buckets are OMITTED from the rendered chart per SR-6 + DR-8 — Bulbasaur
 * has no 4× weakness, so its rendered chart skips the 4× row entirely.
 *
 * Row layout:
 *   - 32 px fixed-width multiplier label column (DM Mono 14/700, top-aligned).
 *   - Type chips wrap inline in the trailing column.
 * Vertical gap between rows: 12 px (CSS, not inline).
 *
 * Type chips delegated to `createEfficacyChip` — the chart consumes the
 * `DefensiveBucketMap` from `data/typeEfficacy.ts:computeDefensiveChips`
 * directly, so dossier MATCHUPS tab data flow is:
 *
 *   await loadGen3()
 *   buckets = computeDefensiveChips([type1, type2], gen.types)
 *   chart = createDefensiveMatchupChart({ buckets })
 *   tabBody.appendChild(chart)
 *
 * Pure DOM. No Phaser dependency.
 */

import { createEfficacyChip } from './efficacyChip';
import type {
  DefensiveBucketKey,
  DefensiveBucketMap,
} from '../data/typeEfficacy';

/** Multiplier-label + numeric-value pairs for each bucket, in render order
 *  (most-vulnerable first per DR-8). */
const BUCKET_RENDER_ORDER: ReadonlyArray<{
  key: DefensiveBucketKey;
  label: string;
  multiplier: number;
}> = [
  { key: '4',    label: '4×', multiplier: 4 },
  { key: '2',    label: '2×', multiplier: 2 },
  { key: '0.5',  label: '½',  multiplier: 0.5 },
  { key: '0.25', label: '¼',  multiplier: 0.25 },
  { key: '0',    label: '0',  multiplier: 0 },
];

export interface DefensiveMatchupChartOptions {
  /** Output of `computeDefensiveChips` — one entry per bucket key. */
  buckets: DefensiveBucketMap;
}

/**
 * Build the `.defensive-matchup-chart` element. Returns an unparented
 * container the caller appends to the MATCHUPS tab body. The element
 * has zero rows when every bucket is empty (effectively unrendered) —
 * pragmatic since no Gen-3 species has zero non-neutral matchups in the
 * 17-type chart.
 */
export function createDefensiveMatchupChart(
  opts: DefensiveMatchupChartOptions,
): HTMLElement {
  const root = document.createElement('div');
  root.className = 'defensive-matchup-chart';

  for (const { key, label, multiplier } of BUCKET_RENDER_ORDER) {
    const types = opts.buckets[key];
    if (!types || types.length === 0) continue; // SR-6 / DR-8: omit empties.

    const row = document.createElement('div');
    row.className = 'defensive-matchup-row';
    row.dataset.bucket = key; // [data-bucket="0.5"] selectors stay valid

    const lbl = document.createElement('span');
    lbl.className = 'defensive-matchup-row__label';
    lbl.textContent = label;
    row.appendChild(lbl);

    const chips = document.createElement('div');
    chips.className = 'defensive-matchup-row__chips';
    for (const t of types) {
      chips.appendChild(createEfficacyChip({ type: t, multiplier }));
    }
    row.appendChild(chips);

    root.appendChild(row);
  }

  return root;
}
